// Shared utility functions for metric calculations

import type { ReportMetricItem } from '@/entities/report'

/**
 * Format a single value based on its key and type
 */
function formatMetricValue(key: string, value: unknown): string {
  if (typeof value !== 'number') {
    return String(value)
  }

  if (key.includes('%')) {
    return value.toFixed(2) + '%'
  } else if (key.toLowerCase().includes('cost')) {
    return value.toFixed(2)
  } else if (key === 'Average handling time') {
    return value.toFixed(1)
  } else if (Number.isInteger(value)) {
    return value.toString()
  } else {
    return value.toFixed(1)
  }
}

/**
 * Calculate enhanced metrics for a single item
 */
export function calculateItemMetrics(
  item: ReportMetricItem,
  options: { raw?: boolean } = {}
): Record<string, number | string> {
  const cost = item.Cost || 0
  const generalMetrics = item.generalMetrics || {}
  const techMetrics = item.techMetrics || {}

  const totalDials = generalMetrics['Total calls'] || 0
  const connectedToAI = generalMetrics['Total calls'] || 0
  const connectedCalls = generalMetrics['Connected calls'] || 0
  const successfulCalls = generalMetrics['Successful calls'] || 0
  const totalMinutes = generalMetrics['Total minutes'] || 0

  const rawMetrics: Record<string, number> = {
    ...generalMetrics,
    ...techMetrics,
    'Total minutes': totalMinutes,
    'Average handling time': connectedToAI > 0 ? (totalMinutes * 60) / connectedToAI : 0,
    'Total calls': totalDials,
    'Connected to AI': connectedToAI,
    'Connected to customer': connectedCalls,
    '% Connected to AI': totalDials > 0 ? (connectedToAI / totalDials) * 100 : 0,
    '% Connected to customer': connectedToAI > 0 ? (connectedCalls / connectedToAI) * 100 : 0,
    '% Successful calls': connectedCalls > 0 ? (successfulCalls / connectedCalls) * 100 : 0,
    'Cost per success': item['Cost per success'] || (successfulCalls > 0 && cost > 0 ? cost / successfulCalls : 0),
    'Cost ($)': cost,
    '% Latency over 2s': totalDials > 0 ? ((techMetrics['Latency over 2s'] || 0) / totalDials) * 100 : 0,
    '% Error': totalDials > 0 ? ((techMetrics['Error'] || 0) / totalDials) * 100 : 0,
  }

  if (options.raw) {
    return rawMetrics
  }

  const formattedMetrics: Record<string, string> = {}
  Object.entries(rawMetrics).forEach(([key, value]) => {
    formattedMetrics[key] = formatMetricValue(key, value)
  })

  return formattedMetrics
}

/**
 * Calculate aggregated metrics for a collection of items
 */
export function calculateAggregatedMetrics(
  data: ReportMetricItem[],
  options: { raw?: boolean } = {}
): Record<string, number | string> {
  const totalCalls = data.reduce((acc, item) => acc + (item.generalMetrics?.['Total calls'] || 0), 0)
  const totalMinutes = data.reduce((acc, item) => acc + (item.generalMetrics?.['Total minutes'] || 0), 0)
  const connectedCalls = data.reduce((acc, item) => acc + (item.generalMetrics?.['Connected calls'] || 0), 0)
  const connectedMinutes = data.reduce((acc, item) => acc + (item.generalMetrics?.['Connected minutes'] || 0), 0)
  const successfulCalls = data.reduce((acc, item) => acc + (item.generalMetrics?.['Successful calls'] || 0), 0)
  const totalCost = data.reduce((acc, item) => acc + (item.Cost || 0), 0)
  const errorCalls = data.reduce((acc, item) => acc + (item.techMetrics?.['Error'] || 0), 0)
  const latencyOver2s = data.reduce((acc, item) => acc + (item.techMetrics?.['Latency over 2s'] || 0), 0)

  const rawMetrics: Record<string, number> = {
    'Total calls': totalCalls,
    'Total minutes': totalMinutes,
    'Average handling time': connectedCalls > 0 ? (connectedMinutes * 60) / connectedCalls : 0,
    'Connected calls': connectedCalls,
    'Connected minutes': connectedMinutes,
    'Successful calls': successfulCalls,
    '% Connected calls': totalCalls > 0 ? (connectedCalls / totalCalls) * 100 : 0,
    '% Successful calls': connectedCalls > 0 ? (successfulCalls / connectedCalls) * 100 : 0,
    'Cost per success': successfulCalls > 0 ? totalCost / successfulCalls : 0,
    'Cost ($)': totalCost,
    Error: errorCalls,
    'Latency over 2s': latencyOver2s,
    '% Latency over 2s': totalCalls > 0 ? (latencyOver2s / totalCalls) * 100 : 0,
    '% Error': totalCalls > 0 ? (errorCalls / totalCalls) * 100 : 0,
  }

  if (options.raw) {
    return rawMetrics
  }

  const formattedMetrics: Record<string, string> = {}
  Object.entries(rawMetrics).forEach(([key, value]) => {
    formattedMetrics[key] = formatMetricValue(key, value)
  })

  return formattedMetrics
}

// Constants
export const UNCONNECTED_DISPOSITIONS = new Set(['DF', 'NA', 'A', 'AM', 'DAIR', 'TECH', 'DROP', 'AA', 'AB'])

/**
 * Standard metric order for display
 */
export const COMMON_METRIC_ORDER = [
  'Total minutes',
  'Average handling time',
  'Total calls',
  'Connected to AI',
  'Connected to customer',
  'Successful calls',
  '% Connected to AI',
  '% Connected to customer',
  '% Successful calls',
  'Cost per success',
  'Cost ($)',
]

/**
 * Process disposition metrics for reports tables
 */
export function processDispositionMetricsForReports(data: ReportMetricItem[], _columnKeys: string[]) {
  // Connected Dispositions
  const connectedDispositionsData = data.map((item) => {
    const dispositions = item.dispositionMetrics || {}
    const connectedCalls = Object.entries(dispositions)
      .filter(([key]) => !key.includes('%') && !UNCONNECTED_DISPOSITIONS.has(key))
      .reduce((sum, [_, value]) => sum + (typeof value === 'number' ? value : parseInt(String(value))), 0)

    return {
      groupKey: item.groupKey,
      ...Object.fromEntries(
        Object.entries(dispositions)
          .filter(([key]) => !key.includes('%') && !UNCONNECTED_DISPOSITIONS.has(key))
          .map(([key, value]) => {
            const numValue = typeof value === 'number' ? value : parseInt(String(value))
            return [key, connectedCalls > 0 ? ((numValue / connectedCalls) * 100).toFixed(2) : '0.00']
          })
      ),
    }
  })

  // Unconnected Dispositions
  const unconnectedDispositionsData = data.map((item) => {
    const dispositions = item.dispositionMetrics || {}
    const totalCalls = Object.entries(dispositions)
      .filter(([key]) => !key.includes('%'))
      .reduce((sum, [_, value]) => sum + (typeof value === 'number' ? value : parseInt(String(value))), 0)

    return {
      groupKey: item.groupKey,
      ...Object.fromEntries(
        Object.entries(dispositions)
          .filter(([key]) => !key.includes('%') && UNCONNECTED_DISPOSITIONS.has(key))
          .map(([key, value]) => {
            const numValue = typeof value === 'number' ? value : parseInt(String(value))
            return [key, totalCalls > 0 ? ((numValue / totalCalls) * 100).toFixed(2) : '0.00']
          })
      ),
    }
  })

  return {
    connectedDispositionsData,
    unconnectedDispositionsData,
  }
}

/**
 * Process duration metrics for reports tables
 */
export function processDurationMetricsForReports(data: ReportMetricItem[]) {
  return data.map((item) => ({
    groupKey: item.groupKey,
    ...Object.fromEntries(
      Object.entries(item.durationMetrics || {})
        .filter(([key]) => key.includes('%'))
        .map(([key, value]) => [key.replace(/[\s%]/g, ''), (value || 0).toFixed(2)])
    ),
  }))
}

/**
 * Sort metric names with numeric prefixes correctly
 * e.g., "% 10s" should come before "% 60s"
 */
export function sortMetricNames(metricNames: string[]): string[] {
  return [...metricNames].sort((a, b) => {
    // Extract numeric parts if present
    const numA = parseInt(a.match(/\d+/)?.[0] || '0')
    const numB = parseInt(b.match(/\d+/)?.[0] || '0')

    if (numA !== numB) {
      return numA - numB
    }
    return a.localeCompare(b)
  })
}